import React, { useState, useEffect, useRef } from "react";
import { Search } from "lucide-react";
import "./CustomFilterSelect.css";

const CustomFilterSelect = ({
  label,
  value,
  onChange,
  options = [],
  isLoading = false,
  disabled = false,
  width = "220px",
  searchable = true,
  searchPlaceholder,
  maxHeight,
  noDataText = "No data found"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState('down');
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (option) => {
    onChange({ target: { value: option.id } });
    setIsOpen(false);
    if (searchable) {
      setSearchQuery("");
    }
  };

  // Smart positioning logic
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 250; // Max dropdown height from CSS

      // Check if we're near the bottom of the viewport or near a table
      const isNearBottom = spaceBelow < dropdownHeight;
      const hasSpaceAbove = spaceAbove > 200; // Minimum space needed above

      if (isNearBottom && hasSpaceAbove) {
        setDropdownPosition('up');
      } else {
        setDropdownPosition('down');
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (options.length) {
      if (searchable && searchQuery.trim()) {
        setFilteredOptions(
          options.filter((opt) => 
            opt.name.toLowerCase().includes(searchQuery.toLowerCase())
          )
        );
      } else {
        setFilteredOptions(options);
      }
    }
  }, [searchQuery, options, searchable]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        if (searchable) {
          setSearchQuery("");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchable]);

  useEffect(() => {
    if (!isOpen && searchable) {
      setSearchQuery("");
    }
  }, [isOpen, searchable]);

  const getDisplayText = () => {
    if (value) {
      const selectedOption = options.find(opt => opt.id === value);
      return selectedOption?.name || label;
    }
    return label;
  };

  const getSearchPlaceholder = () => {
    if (searchPlaceholder) {
      return searchPlaceholder;
    }
    return `Search ${label.toLowerCase()}`;
  };

  return (
    <div
      className={`custom-filter-select-container ${disabled ? "custom-disabled" : ""}`}
      style={{ width }}
      ref={dropdownRef}
    >
      <div 
        ref={containerRef}
        className="custom-filter-select" 
        onClick={handleToggleDropdown}
      >
        <span className="custom-filter-label">{getDisplayText()}</span>
        <span className={`custom-filter-select-arrow `}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="#4B5563"
            className={`w-5 h-5 ${isOpen ? 'rotate-180' : ''}`}
            style={{ width: '16px', height: '16px' }}
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </div>

      {isOpen && (
        <div 
          className={`custom-filter-select-dropdown ${dropdownPosition === 'up' ? 'open-upward' : ''}`}
        >
          {searchable && (
            <div className="custom-filter-select-search">
              <input
                className="search-input"
                placeholder={getSearchPlaceholder()}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
              <Search width={18} />
            </div>
          )}

          <div className="custom-filter-select-options" style={maxHeight ? { maxHeight: maxHeight } : {}}>
            {isLoading ? (
              <div className="custom-filter-select-option disabled">Loading...</div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <React.Fragment key={option.id}>
                  <div
                    className={`custom-filter-select-option ${value === option.id ? 'selected' : ''}`}
                    onClick={() => handleOptionClick(option)}
                  >
                    {option.name}
                  </div>
                  {option.id === '' && index === 0 && filteredOptions.length > 1 && (
                    <div className="custom-filter-select-divider"></div>
                  )}
                </React.Fragment>
              ))
            ) : (
              <div className="custom-filter-select-option disabled">
                {searchable && searchQuery.trim() ? `No results for "${searchQuery}"` : noDataText}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomFilterSelect;